import { useEffect, useRef, useState } from 'react';
import { JOBS } from '../../../mocks/jobs';
import { fireSwipeEvent } from '../utils/tracking';
import { ACCENT, ACCENT_DARK, TEAL, BG_WARM } from '../utils/theme';

const ACCENT_COLORS: Record<string, string> = {
  hospital: '#4CAFA0',
  clinic:   '#E88890',
  homecare: '#C4A8D4',
  rehab:    '#7BBFCA',
};

interface JobCardsSectionProps {
  onOpenModal: () => void;
}

export default function JobCardsSection({ onOpenModal }: JobCardsSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const prevActiveIndex = useRef(-1);
  const totalCards = JOBS.length + 1;

  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const dragDistance = useRef(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const cardWidth = el.scrollWidth / totalCards;
      const idx = Math.round(el.scrollLeft / cardWidth);
      setActiveIndex(Math.min(idx, totalCards - 1));
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [totalCards]);

  useEffect(() => {
    if (prevActiveIndex.current === -1) {
      prevActiveIndex.current = activeIndex;
      return;
    }
    if (prevActiveIndex.current === activeIndex) return;
    prevActiveIndex.current = activeIndex;
    if (activeIndex < JOBS.length) {
      const job = JOBS[activeIndex];
      fireSwipeEvent({ cardId: job.id, cardIndex: activeIndex + 1, cardName: job.departments });
    } else {
      fireSwipeEvent({ cardId: 'more', cardIndex: activeIndex + 1, cardName: 'もっと見たい方はこちら' });
    }
  }, [activeIndex]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragScrollLeft.current = el.scrollLeft;
    dragDistance.current = 0;
    el.style.cursor = 'grabbing';
    const handleDocMove = (ev: PointerEvent) => {
      if (!isDragging.current || !scrollRef.current) return;
      dragDistance.current = Math.abs(ev.clientX - dragStartX.current);
      scrollRef.current.scrollLeft = dragScrollLeft.current + (dragStartX.current - ev.clientX);
    };
    const handleDocUp = () => {
      isDragging.current = false;
      if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
      document.removeEventListener('pointermove', handleDocMove);
      document.removeEventListener('pointerup', handleDocUp);
    };
    document.addEventListener('pointermove', handleDocMove);
    document.addEventListener('pointerup', handleDocUp);
  };

  const handleCardTap = (callback: () => void) => {
    if (dragDistance.current < 8) callback();
  };

  const scrollToIndex = (idx: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.scrollWidth / totalCards;
    el.scrollTo({ left: cardWidth * idx, behavior: 'smooth' });
  };

  return (
    <section
      style={{ background: BG_WARM, paddingBottom: 8 }}
      data-track-view="true"
      data-element-id="job_cards_section"
      data-element-type="section"
      data-element-name="求人カードセクション"
      data-element-index="0"
    >
      <div style={{ maxWidth: 430, margin: '0 auto', padding: '28px 20px 4px' }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: '#2a2a2a', lineHeight: 1.5, marginBottom: 6 }}>
          ご紹介できる求人の一例
        </h2>
        <p
          style={{
            fontSize: 11,
            color: ACCENT,
            fontWeight: 600,
            background: `${ACCENT}12`,
            borderRadius: 8,
            padding: '7px 12px',
            display: 'inline-block',
            lineHeight: 1.6,
          }}
        >
          ※ これ以外にも非公開を含む3,000件以上の求人があります。<br />
          担当者がご希望にあった求人をご案内します。
        </p>
      </div>

      <div
        ref={scrollRef}
        onPointerDown={handlePointerDown}
        style={{
          display: 'flex',
          overflowX: 'scroll',
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth',
          gap: 12,
          padding: '12px 20px 8px',
          maxWidth: 430,
          margin: '0 auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          cursor: 'grab',
          userSelect: 'none',
          touchAction: 'pan-x',
        }}
        className="scroll-hide"
      >
        {JOBS.map((job, idx) => (
          <JobCard key={job.id} job={job} index={idx + 1} />
        ))}

        <div
          onClick={() => handleCardTap(onOpenModal)}
          data-track-tap="true"
          data-element-id="job_card_more"
          data-element-type="card"
          data-element-name="もっと見たい方はこちら"
          data-element-index={JOBS.length + 1}
          style={{
            flexShrink: 0,
            width: 'min(85vw, 346px)',
            scrollSnapAlign: 'start',
            borderRadius: 16,
            background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 24px',
            cursor: 'pointer',
            boxShadow: `0 2px 12px ${ACCENT}50`,
            minHeight: 320,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 16,
            }}
          >
            <i className="ri-search-line" style={{ fontSize: 24, color: '#fff' }} />
          </div>
          <p style={{ fontSize: 16, fontWeight: 700, color: '#fff', textAlign: 'center', lineHeight: 1.6, marginBottom: 20 }}>
            もっと見たい方はこちら
          </p>
          <div
            style={{
              background: '#fff',
              color: ACCENT_DARK,
              borderRadius: 24,
              padding: '10px 24px',
              fontSize: 13,
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            希望条件を伝えて探す
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 12 }}>
        {Array.from({ length: totalCards }).map((_, i) => (
          <button
            key={i}
            onClick={() => scrollToIndex(i)}
            style={{
              width: i === activeIndex ? 20 : 7,
              height: 7,
              borderRadius: 4,
              background: i === activeIndex ? ACCENT : '#ddd',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              transition: 'all 0.25s ease',
            }}
          />
        ))}
      </div>

      <div style={{ maxWidth: 430, margin: '20px auto 0', padding: '0 20px 28px' }}>
        <button
          onClick={onOpenModal}
          data-track-tap="true"
          data-element-id="cta_after_jobs"
          data-element-type="button"
          data-element-name="求人カード下CTA"
          data-element-index="0"
          style={{
            width: '100%',
            background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`,
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            padding: '17px 24px',
            fontSize: 15,
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: `0 4px 18px ${ACCENT}55`,
            whiteSpace: 'nowrap',
            letterSpacing: '0.01em',
          }}
        >
          近いうちに条件を良くしたい
        </button>
      </div>
    </section>
  );
}

function JobCard({ job, index }: { job: (typeof JOBS)[0]; index: number }) {
  const accentColor = ACCENT_COLORS[job.accentType] || TEAL;

  return (
    <div
      data-track-tap="true"
      data-element-id={`job_card_${job.id}`}
      data-element-type="card"
      data-element-name={`求人カード_${job.id}`}
      data-element-index={index}
      style={{
        flexShrink: 0,
        width: 'min(85vw, 346px)',
        scrollSnapAlign: 'start',
        borderRadius: 14,
        background: '#fff',
        border: '1px solid #ececec',
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
      }}
    >
      {/* ヘッダー：パステル背景 + バッジ + 施設情報 */}
      <div
        style={{
          background: `${accentColor}18`,
          padding: '10px 13px 11px',
        }}
      >
        {job.badge && (
          <span
            style={{
              display: 'inline-block',
              background: accentColor,
              color: '#fff',
              borderRadius: 20,
              padding: '2px 10px',
              fontSize: 10,
              fontWeight: 700,
              marginBottom: 7,
              letterSpacing: '0.03em',
            }}
          >
            {job.badge}
          </span>
        )}
        {!job.badge && <div style={{ height: 0, marginBottom: 0 }} />}
        <div style={{ fontSize: 10, color: '#999', marginBottom: 2, lineHeight: 1 }}>
          {job.location}
        </div>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#2a2a2a', lineHeight: 1.3 }}>
          {job.facilityType}
        </div>
      </div>

      {/* コンテンツ：月収・残業・業務内容・タグ */}
      <div style={{ padding: '10px 13px 12px' }}>
        {/* 月収 */}
        <div
          style={{
            fontSize: 26,
            fontWeight: 900,
            color: '#E8606E',
            lineHeight: 1,
            marginBottom: 9,
            letterSpacing: '-0.01em',
          }}
        >
          {job.monthlyIncome}
        </div>

        {/* 月残業時間 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
          <div style={{ width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="ri-time-line" style={{ fontSize: 12, color: '#bbb' }} />
          </div>
          <span style={{ fontSize: 11, color: '#888' }}>
            月残業時間: {job.overtime ?? '記載なし'}
          </span>
        </div>

        {/* 業務内容 */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, marginBottom: 10 }}>
          <div style={{ width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
            <i className="ri-clipboard-line" style={{ fontSize: 12, color: '#bbb' }} />
          </div>
          <span style={{ fontSize: 11, color: '#555', fontWeight: 500, lineHeight: 1.5 }}>
            {job.departments}
          </span>
        </div>

        {/* タグ */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {job.tags.map((tag) => (
            <span
              key={tag}
              style={{
                display: 'inline-block',
                background: '#fff',
                border: `1.5px solid ${accentColor}`,
                color: accentColor,
                borderRadius: 20,
                padding: '3px 10px',
                fontSize: 10,
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
